import { useDraggable } from '@dnd-kit/react'
import type { GroupingItem } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import cn from 'classnames'
import { useContext } from 'use-context-selector'

import GroupingContext from 'components/Editor/EditorElements/elements/Grouping/GroupingContext'
import {
  borderRadiusContent,
  cardStyles,
} from 'components/Editor/EditorElements/elements/Grouping/helper'
import PortalGroupingItemStyled, {
  AnswerDragIconStyled,
} from 'components/Editor/EditorElements/elements/Grouping/styled/PortalGroupingItemStyled'
import { getObjectFitDivMap } from 'components/Editor/EditorElements/utils/getObjectFitDivMap'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { useProjectT } from 'services/ProjectLabels'
import { useUrlByUsage } from 'utils/files'

import * as s from './PortalGroupingItem.module.scss'

export type ValidationMap = {
  [key: string]: string
}

type ItemProps = {
  isActive?: boolean
  item: GroupingItem
}

const PortalGroupingItem = ({ isActive, item }: ItemProps) => {
  const pt = useProjectT()
  const { styles, hasAnswerImage, font, pdfStyles } = useContext(GroupingContext)
  const { ref, isDragging } = useDraggable({
    id: item.value,
    data: item,
    disabled: !isActive,
  })

  const imageUrl = useUrlByUsage(item.image, FileUsageTypeEnum.image)
  const backgroundImage = (hasAnswerImage && `url(${imageUrl})`) || undefined
  const textStyles = item.label && { ...font?.base }
  const labelClasses = cn(s.label, { [s.placeholder]: !item.label })
  const label =
    item.label || (item?.image?.id && hasAnswerImage)
      ? item.label || ''
      : pt('elements.grouping.answer.placeholder')

  const childStyles = {
    ...cardStyles,
    ...styles.answer,
    backgroundImage,
    backgroundSize: getObjectFitDivMap(item.image?.objectFit),
    ...pdfStyles,
    opacity: isDragging ? 0.4 : 1,
  }

  return (
    <PortalGroupingItemStyled
      $styles={styles}
      aria-hidden={!isActive}
      aria-label={item.label || item?.image?.accessibility || item.value}
      data-grouping-id={item.value}
      ref={ref as React.RefCallback<HTMLDivElement>}
      style={childStyles}
      tabIndex={isActive ? 0 : -1}
    >
      <div
        className={s.mask}
        style={{
          backgroundColor: item.overlay || undefined,
          borderRadius: borderRadiusContent(styles.answer) || styles.answer.borderRadius,
        }}
      />
      <div className={labelClasses} style={{ ...textStyles, padding: styles.indents.padding }}>
        {label}
      </div>
      <div className={s.iconWrapper}>
        {styles.icon.backgroundImage ? (
          <div className={s.icon} style={styles.icon} />
        ) : (
          <AnswerDragIconStyled $isDragging={isDragging} $styles={styles}>
            <Icon name='builderDrug' size={KitSize.S} />
          </AnswerDragIconStyled>
        )}
      </div>
    </PortalGroupingItemStyled>
  )
}

export default PortalGroupingItem
